// OpportunityCarousel — Swipeable cards for Space/Improvement/Appeal breakdown
import { useState } from "react";
import { Maximize2, Building2, BarChart3, Shield, ChevronLeft, ChevronRight } from "lucide-react";
import { SCORE_DETAILS } from "@/pages/hippo/constants";

const CARDS = [
  { key: "spatial_efficiency", label: "Space Utilisation", icon: Maximize2, color: "#006A4D" },
  { key: "unlockability", label: "Improvement Potential", icon: Building2, color: "#C5A059" },
  { key: "market_alignment", label: "Market Appeal", icon: BarChart3, color: "#3B82F6" },
];

function getScoreContext(key, value) {
  if (key === "spatial_efficiency") {
    if (value >= 70) return "Your home has significant space that could be reconfigured to add value.";
    if (value >= 50) return "There's moderate room to improve how your space is used.";
    return "Your space is fairly well utilised — gains are limited here.";
  }
  if (key === "unlockability") {
    if (value >= 70) return "Your property has strong features that make valuable improvements practical.";
    if (value >= 50) return "Some improvement pathways are available, depending on scope.";
    return "Fewer options for major improvements — focus on targeted changes.";
  }
  if (value >= 70) return "Improvements would be well received by your local market.";
  if (value >= 50) return "Your area shows moderate appetite for improved properties.";
  return "Local market conditions may limit the impact of improvements.";
}

export function OpportunityCarousel({ scores, confidence }) {
  const [activeIdx, setActiveIdx] = useState(0);

  const prev = () => setActiveIdx(i => Math.max(0, i - 1));
  const next = () => setActiveIdx(i => Math.min(CARDS.length - 1, i + 1));

  const card = CARDS[activeIdx];
  const value = Math.round(scores[card.key] || 50);
  const detail = SCORE_DETAILS[card.key];
  const context = getScoreContext(card.key, value);
  const Icon = card.icon;

  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div data-testid="opportunity-carousel">
      <p className="text-[10px] text-white/40 uppercase tracking-wider text-center mb-3">Opportunity Breakdown</p>

      <div className="relative">
        {/* Navigation arrows */}
        <button
          onClick={prev}
          disabled={activeIdx === 0}
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
            activeIdx === 0 ? 'opacity-0' : 'bg-white/10 hover:bg-white/20'
          }`}
          data-testid="carousel-prev"
        >
          <ChevronLeft className="w-4 h-4 text-white/60" />
        </button>
        <button
          onClick={next}
          disabled={activeIdx === CARDS.length - 1}
          className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
            activeIdx === CARDS.length - 1 ? 'opacity-0' : 'bg-white/10 hover:bg-white/20'
          }`}
          data-testid="carousel-next"
        >
          <ChevronRight className="w-4 h-4 text-white/60" />
        </button>

        {/* Card */}
        <div className="mx-8 bg-white/[0.04] border border-white/5 rounded-xl p-4 transition-all duration-300" data-testid={`carousel-card-${card.key}`}>
          <div className="flex items-center gap-4">
            {/* Score ring */}
            <div className="relative w-20 h-20 flex-shrink-0">
              <svg viewBox="0 0 80 80" className="transform -rotate-90">
                <circle cx="40" cy="40" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
                <circle
                  cx="40" cy="40" r={radius}
                  fill="none" stroke={card.color} strokeWidth="5" strokeLinecap="round"
                  strokeDasharray={circumference} strokeDashoffset={offset}
                  style={{ transition: "stroke-dashoffset 0.6s ease-out" }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-white">{value}</span>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5 text-white/50" />
                <span className="text-sm font-medium text-white/80">{card.label}</span>
              </div>
              <p className="text-[11px] text-white/40 leading-relaxed">{detail?.description}</p>
              <p className="text-[11px] text-white/55 leading-relaxed mt-1.5">{context}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex items-center justify-center gap-2 mt-3">
        {CARDS.map((c, i) => (
          <button
            key={c.key}
            onClick={() => setActiveIdx(i)}
            className={`rounded-full transition-all ${
              i === activeIdx ? 'w-5 h-1.5 bg-white/50' : 'w-1.5 h-1.5 bg-white/20'
            }`}
            data-testid={`carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}
